// frontend/src/pages/CustomerReturns.jsx
import React, { useState, useEffect } from "react";
import { Search, Filter, Calendar, Package, ArrowRight, AlertTriangle, CheckCircle, Clock, RefreshCw, Eye, Edit, Trash2, Plus } from "lucide-react";
import trackingAPI from "../api/trackingAPI";
import axios from "axios";
import "./CustomerReturns.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";

const CustomerReturns = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedReturns, setSelectedReturns] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);

  // AWB-based return state
  const [awbNumber, setAwbNumber] = useState("");
  const [saleData, setSaleData] = useState(null);
  const [saleItems, setSaleItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [returnQuantity, setReturnQuantity] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [fetchingSale, setFetchingSale] = useState(false);

  // New return form state
  const [newReturn, setNewReturn] = useState({
    orderId: "",
    awbNumber: "",
    customerName: "",
    product: "",
    returnReason: "",
    productCondition: "good",
    status: "pending",
    returnDate: new Date().toISOString().slice(0, 10),
    notes: ""
  });

  // Move to manufacturing form state
  const [manufacturingData, setManufacturingData] = useState({
    damageStatus: "none",
    notes: "",
    estimatedCost: 0
  });

  useEffect(() => {
    loadCustomerReturns();
  }, []);

  // ✅ AWB Auto-fetch for original form
  const fetchSaleForForm = async (awbNumber) => {
    if (!awbNumber.trim()) {
      return;
    }

    try {
      const response = await axios.get(`${API_BASE}/customer-returns/sale/${awbNumber}`);
      const data = response.data;

      // Auto-fill form fields
      setNewReturn(prev => ({
        ...prev,
        orderId: data.sale.id,
        customerName: data.sale.customer_name,
        product: data.items.length > 0 ? data.items[0].product_name : ""
      }));

    } catch (error) {
      console.error("Error fetching sale for form:", error);
      // Don't show alert for form auto-fetch, just log it
    }
  };

  // Fetch sale by AWB number
  const fetchSaleByAWB = async () => {
    if (!awbNumber.trim()) {
      alert("Please enter AWB number");
      return;
    }

    setFetchingSale(true);
    try {
      const response = await axios.get(`${API_BASE}/customer-returns/sale/${awbNumber}`);
      const data = response.data;

      setSaleData(data.sale);
      setSaleItems(data.items);
      setSelectedItem(null);
      setReturnQuantity("");
      setReturnReason("");
    } catch (error) {
      console.error("Error fetching sale:", error);
      alert(error.response?.data?.message || "Sale not found for this AWB number");
      setSaleData(null);
      setSaleItems([]);
    } finally {
      setFetchingSale(false);
    }
  };

  // Process customer return
  const processReturn = async () => {
    if (!selectedItem || !returnQuantity || !returnReason) {
      alert("Please fill all fields");
      return;
    }

    const quantity = parseInt(returnQuantity);
    if (quantity <= 0) {
      alert("Return quantity must be greater than 0");
      return;
    }

    if (quantity > selectedItem.quantity) {
      alert(`Cannot return ${quantity} items. Only ${selectedItem.quantity} items were sold.`);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/customer-returns/process`, {
        order_id: saleData.id,
        item_id: selectedItem.inventory_id,
        product: selectedItem.product_name,
        quantity: quantity,
        awb_number: awbNumber,
        return_reason: returnReason,
        sale_id: saleData.id
      });

      alert("Return processed successfully!");
      
      // Reset form
      setAwbNumber("");
      setSaleData(null);
      setSaleItems([]);
      setSelectedItem(null);
      setReturnQuantity("");
      setReturnReason("");
      
      // Reload returns
      loadCustomerReturns();
    } catch (error) {
      console.error("Error processing return:", error);
      alert(error.response?.data?.message || "Error processing return");
    }
  };

  const loadCustomerReturns = async () => {
    setLoading(true);
    try {
      const data = await trackingAPI.getCustomerReturns();
      console.log("Customer Returns API Response:", data);
      setReturns(data || []);
    } catch (error) {
      console.error('Error loading customer returns:', error);
      // Fallback mock data
      setReturns([
        {
          id: 1,
          orderId: "ORD-001",
          awbNumber: "AWB123456789",
          customerName: "Rahul Sharma",
          product: "Wireless Headphones",
          returnReason: "Defective Product",
          productCondition: "damaged",
          status: "pending",
          returnDate: "2025-01-27",
          notes: "Customer reported audio issues"
        },
        {
          id: 2,
          orderId: "ORD-002",
          awbNumber: "AWB987654321",
          customerName: "Priya Patel",
          product: "Smart Watch",
          returnReason: "Wrong Item",
          productCondition: "good",
          status: "approved",
          returnDate: "2025-01-26",
          notes: "Customer received different color"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReturn = async () => {
    if (!newReturn.awbNumber || !newReturn.product || !newReturn.returnReason || !newReturn.productCondition || !newReturn.status || !newReturn.returnDate) {
      alert("Please fill all required fields");
      return;
    }

    try {
      // ✅ Use new simple API
      await axios.post(`${API_BASE}/customer-returns/process`, {
        awb_number: newReturn.awbNumber,
        product: newReturn.product,
        return_reason: newReturn.returnReason,
        product_condition: newReturn.productCondition,
        status: newReturn.status,
        return_date: newReturn.returnDate
      });
      
      setShowAddModal(false);
      setNewReturn({
        orderId: "",
        awbNumber: "",
        customerName: "",
        product: "",
        returnReason: "",
        productCondition: "good",
        status: "pending",
        returnDate: new Date().toISOString().slice(0, 10),
        notes: ""
      });
      loadCustomerReturns();
      alert("Return created successfully!");
    } catch (error) {
      console.error("Error creating return:", error);
      alert("Failed to create return: " + (error.response?.data?.message || error.message));
    }
  };

  const handleMoveToManufacturing = async () => {
    if (!selectedReturn) return;

    try {
      await trackingAPI.moveToManufacturing(selectedReturn.id, manufacturingData);
      setShowMoveModal(false);
      setManufacturingData({
        damageStatus: "none",
        notes: "",
        estimatedCost: 0
      });
      loadCustomerReturns();
      alert("Moved to manufacturing successfully!");
    } catch (error) {
      console.error('Error moving to manufacturing:', error);
      alert("Failed to move to manufacturing. Please try again.");
    }
  };

  const filteredReturns = returns.filter(returnItem => {
    const matchesSearch = searchQuery === "" || 
      (returnItem.order_id || returnItem.orderId || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (returnItem.awb_number || returnItem.awbNumber || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (returnItem.customer_name || returnItem.customerName || "").toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || returnItem.status === statusFilter;
    
    const matchesDate = dateFilter === "" || returnItem.returnDate === dateFilter;
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "yellow", icon: <Clock size={14} />, text: "Pending" },
      approved: { color: "blue", icon: <CheckCircle size={14} />, text: "Approved" },
      processing: { color: "purple", icon: <RefreshCw size={14} />, text: "Processing" },
      completed: { color: "green", icon: <CheckCircle size={14} />, text: "Completed" },
      rejected: { color: "red", icon: <AlertTriangle size={14} />, text: "Rejected" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`status-badge status-${config.color}`}>
        {config.icon}
        {config.text}
      </span>
    );
  };

  const getConditionBadge = (productCondition) => {
    const conditionConfig = {
      good: { color: "green", text: "Good" },
      damaged: { color: "orange", text: "Damaged" },
      defective: { color: "red", text: "Defective" },
      used: { color: "yellow", text: "Used" }
    };
    
    const config = conditionConfig[productCondition] || conditionConfig.good;
    return (
      <span className={`condition-badge condition-${config.color}`}>
        {config.text}
      </span>
    );
  };

  const toggleSelectReturn = (id) => {
    const newSelected = new Set(selectedReturns);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedReturns(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedReturns.size === filteredReturns.length) {
      setSelectedReturns(new Set());
    } else {
      setSelectedReturns(new Set(filteredReturns.map(r => r.id)));
    }
  };

  return (
    <div className="customer-returns-container">
      <div className="returns-header">
        <div className="header-left">
          <h1 className="returns-title">Customer Returns</h1>
          <p className="returns-subtitle">Manage customer returns and move to manufacturing</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus size={18} />
            Add Return
          </button>
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-grid">
          <div className="search-group">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by Order ID, AWB, or Customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="filter-input"
          />
        </div>
      </div>

      <div className="returns-table-card">
        <div className="table-header">
          <div className="table-info">
            <span className="selected-count">
              {selectedReturns.size} of {filteredReturns.length} selected
            </span>
          </div>
          <div className="table-actions">
            {selectedReturns.size > 0 && (
              <button className="btn btn-outline">
                Bulk Actions
              </button>
            )}
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <div className="loading-message">Loading returns...</div>
          ) : (
            <table className="returns-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedReturns.size === filteredReturns.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Order ID</th>
                  <th>AWB Number</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Return Reason</th>
                  <th>Condition</th>
                  <th>Status</th>
                  <th>Return Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReturns.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-message">
                      No returns found
                    </td>
                  </tr>
                ) : (
                  filteredReturns.map((returnItem) => (
                    <tr key={returnItem.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedReturns.has(returnItem.id)}
                          onChange={() => toggleSelectReturn(returnItem.id)}
                        />
                      </td>
                      <td className="order-id">{returnItem.order_id || returnItem.orderId || "-"}</td>
                      <td className="awb-number">{returnItem.awb_number || returnItem.awbNumber || "-"}</td>
                      <td>{returnItem.customer_name || returnItem.customerName || "-"}</td>
                      <td>{returnItem.product}</td>
                      <td>{returnItem.returnReason}</td>
                      <td>{getConditionBadge(returnItem.productCondition)}</td>
                      <td>{getStatusBadge(returnItem.status)}</td>
                      <td>{returnItem.returnDate}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button 
                            className="btn-icon"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          {returnItem.status === 'approved' && (
                            <button 
                              className="btn-icon btn-primary"
                              title="Move to Manufacturing"
                              onClick={() => {
                                setSelectedReturn(returnItem);
                                setShowMoveModal(true);
                              }}
                            >
                              <ArrowRight size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Return Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Add Customer Return</h3>
              <button className="btn-close" onClick={() => setShowAddModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Order ID *</label>
                  <input
                    type="text"
                    value={newReturn.orderId}
                    onChange={(e) => setNewReturn({...newReturn, orderId: e.target.value})}
                    placeholder="e.g., ORD-001"
                  />
                </div>
                
                <div className="form-group">
                  <label>AWB Number *</label>
                  <input
                    type="text"
                    value={newReturn.awbNumber}
                    onChange={(e) => {
                      setNewReturn({...newReturn, awbNumber: e.target.value});
                      fetchSaleForForm(e.target.value);
                    }}
                    placeholder="e.g., AWB123456789"
                  />
                </div>
                
                <div className="form-group">
                  <label>Customer *</label>
                  <input
                    type="text"
                    value={newReturn.customerName}
                    onChange={(e) => setNewReturn({...newReturn, customerName: e.target.value})}
                    placeholder="Customer name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Product</label>
                  <input
                    type="text"
                    value={newReturn.product}
                    onChange={(e) => setNewReturn({...newReturn, product: e.target.value})}
                    placeholder="Product name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Return Reason</label>
                  <select
                    value={newReturn.returnReason}
                    onChange={(e) => setNewReturn({...newReturn, returnReason: e.target.value})}
                  >
                    <option value="">Select reason</option>
                    <option value="Defective Product">Defective Product</option>
                    <option value="Wrong Item">Wrong Item</option>
                    <option value="Damaged in Transit">Damaged in Transit</option>
                    <option value="Not as Described">Not as Described</option>
                    <option value="Customer Dissatisfaction">Customer Dissatisfaction</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Condition</label>
                  <select
                    value={newReturn.productCondition}
                    onChange={(e) => setNewReturn({...newReturn, productCondition: e.target.value})}
                  >
                    <option value="good">Good</option>
                    <option value="damaged">Damaged</option>
                    <option value="used">Used</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newReturn.status}
                    onChange={(e) => setNewReturn({...newReturn, status: e.target.value})}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Return Date</label>
                  <input
                    type="date"
                    value={newReturn.returnDate}
                    onChange={(e) => setNewReturn({...newReturn, returnDate: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={newReturn.notes}
                    onChange={(e) => setNewReturn({...newReturn, notes: e.target.value})}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreateReturn}>
                Add Return
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move to Manufacturing Modal */}
      {showMoveModal && selectedReturn && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Move to Manufacturing</h3>
              <button className="btn-close" onClick={() => setShowMoveModal(false)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="return-summary">
                <h4>Return Details</h4>
                <div className="summary-grid">
                  <div><strong>Order ID:</strong> {selectedReturn.orderId}</div>
                  <div><strong>Product:</strong> {selectedReturn.product}</div>
                  <div><strong>Customer:</strong> {selectedReturn.customerName}</div>
                  <div><strong>Condition:</strong> {getConditionBadge(selectedReturn.productCondition)}</div>
                </div>
              </div>
              
              <div className="manufacturing-form">
                <h4>Manufacturing Details</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Damage Status</label>
                    <select
                      value={manufacturingData.damageStatus}
                      onChange={(e) => setManufacturingData({...manufacturingData, damageStatus: e.target.value})}
                    >
                      <option value="none">None</option>
                      <option value="minor">Minor</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                      <option value="total_loss">Total Loss</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Estimated Repair Cost</label>
                    <input
                      type="number"
                      value={manufacturingData.estimatedCost}
                      onChange={(e) => setManufacturingData({...manufacturingData, estimatedCost: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label>Notes</label>
                    <textarea
                      value={manufacturingData.notes}
                      onChange={(e) => setManufacturingData({...manufacturingData, notes: e.target.value})}
                      placeholder="Manufacturing notes..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowMoveModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleMoveToManufacturing}>
                Move to Manufacturing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerReturns;
